
import logger from "@/lib/logger";
import { toast } from "@/hooks/use-toast";
import { GenerateProposalResult } from "./api/proposal-types";
import { performConnectivityCheck } from "./api/connectivity-checker";
import { callProposalEdgeFunction } from "./api/edge-function-caller";

export const generateProposalApi = async (
  jobDescription: string,
  profileTitle: string = "",
  profileOverview: string = ""
): Promise<GenerateProposalResult> => {
  const descriptionLength = jobDescription.trim().length;
  logger.log("Starting proposal generation API call with description length:", descriptionLength);
  
  try {
    // Show initial toast notification
    toast({
      title: "Starting AI service...",
      description: "Checking your connection and preparing the AI service.",
      duration: 3000,
    });
    
    // Enhanced internet connectivity check
    const isConnected = await performConnectivityCheck();
    
    // Handle no internet connection
    if (!isConnected) {
      return {
        proposal: "",
        error: "Internet connection not detected. Please check your network settings and try again.",
        isQuotaExceeded: false,
        isRateLimited: false,
        isFallback: false
      };
    }
    
    logger.log("Proceeding with AI request.");
    
    toast({
      title: "Generating your proposal...",
      description: "Our AI is crafting a tailored proposal based on your input.",
      duration: 3000,
    });
    
    // Function call with timeout and retry logic
    const timeoutDuration = 180000; // 3 minutes for GPT-4o
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await callProposalEdgeFunction({
          jobDescription,
          profileTitle,
          profileOverview,
          timeoutDuration,
          attempt,
          maxRetries
        });
        
        return result;
      } catch (retryError) {
        lastError = retryError;
        logger.error(`Attempt ${attempt + 1} failed:`, retryError.message);
        
        // If this was the last retry, continue to throw the error
        if (attempt >= maxRetries) {
          break;
        }
      }
    }
    
    // If we got here, all retries failed
    const errorMessage = lastError?.message || "All retry attempts failed";
    logger.error("All retries failed with error:", errorMessage);
    
    // Different messages based on error type
    if (errorMessage.includes("timed out") || errorMessage.includes("aborted")) {
      toast({
        title: "Request Timeout",
        description: "The AI service is taking too long to respond. Please try again later.",
        variant: "destructive",
      });
      
      return {
        proposal: "",
        error: "Request timed out. The AI service might be busy. Please try again in a few moments.",
        isQuotaExceeded: false,
        isRateLimited: false,
        isFallback: false
      };
    } else if (
      errorMessage.includes("network") || 
      errorMessage.includes("fetch") || 
      errorMessage.includes("connect") ||
      errorMessage.includes("CORS")
    ) {
      toast({
        title: "Network Connection Error",
        description: "Unable to connect to the AI service. Please check your internet connection.",
        variant: "destructive",
      });
      
      return {
        proposal: "",
        error: "Network error: Unable to connect to AI service. This might be a CORS or connectivity issue. Please try again later.",
        isQuotaExceeded: false,
        isRateLimited: false,
        isFallback: false
      };
    }
    
    // Generic error
    return {
      proposal: "",
      error: errorMessage,
      isQuotaExceeded: false,
      isRateLimited: false,
      isFallback: false
    };
  } catch (error) {
    logger.error("Error generating proposal:", error);
    
    return {
      proposal: "",
      error: error.message || "An unexpected error occurred. Please try again later.",
      isQuotaExceeded: false,
      isRateLimited: false,
      isFallback: false
    };
  }
};
